import React, {useState, useEffect} from 'react'
import {HashRouter as Router, Routes, Route, Navigate} from 'react-router-dom'
import {motion} from 'framer-motion'
import Login from './components/Login'
import Header from './components/Header'
import Sidebar from './components/Sidebar'
import Profile from './components/Profile'
import ProfileModal from './components/ProfileModal'
import CourseSelector from './components/CourseSelector'
import ArchivedReports from './components/ArchivedReports'
import ReportExport from './components/ReportExport'
import SetPLO from './components/SetPLO'
import AssessmentPlan from './components/AssessmentPlan'
import SLTWeightage from './components/SLTWeightage'
import CLOPLOMapping from './components/CLOPLOMapping'
import MarksEntry from './components/MarksEntry'
import CoursePerformance from './components/CoursePerformance'
import CLOAnalysis from './components/CLOAnalysis'
import PLOAnalysis from './components/PLOAnalysis'
import FailureReport from './components/FailureReport'
import {AppProvider, useApp} from './context/AppContext'
import './App.css'

const AppContent = () => {
  const {state, dispatch, loadCourseData, authService, courseService} = useApp()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [showExportModal, setShowExportModal] = useState(false)
  const [showProfileModal, setShowProfileModal] = useState(false)
  const [exportCourse, setExportCourse] = useState(null)

  const handleLogin = async (user, profile) => {
    dispatch({type: 'SET_USER', payload: user})
    dispatch({type: 'SET_PROFILE', payload: profile})
  }

  const handleLogout = async () => {
    await authService.signOut()
    dispatch({type: 'LOGOUT'})
  }

  const handleUpdateProfile = async (profileData) => {
    try {
      const {data, error} = await authService.updateProfile(state.user.id, profileData)
      if (error) throw new Error(error)
      dispatch({type: 'SET_PROFILE', payload: data})
    } catch (error) {
      dispatch({type: 'SET_ERROR', payload: error.message})
    }
  }

  const handleSelectCourse = async (course) => {
    dispatch({type: 'SET_ACTIVE_COURSE', payload: course})
    await loadCourseData(course.id)
  }

  const handleDeleteCourse = async (courseId) => {
    // If the active course was deleted, reset active course
    if (state.activeCourse && state.activeCourse.id === courseId) {
      dispatch({type: 'SET_ACTIVE_COURSE', payload: null})
    }
  }

  const handleCompleteReport = (course) => {
    setExportCourse(course)
    setShowExportModal(true)
  }

  const handleExportReport = async (course, format) => {
    try {
      // Update course status to completed
      const {error} = await courseService.completeCourse(course.id)
      if (error) throw new Error(error)

      const reportData = {
        ...course,
        completedAt: new Date().toISOString(),
        format
      }

      dispatch({type: 'COMPLETE_COURSE', payload: reportData})

      // Simulate download
      const filename = `${course.course_code || course.courseCode}_Report.${format}`
      alert(`Report exported successfully as ${filename}`)
    } catch (error) {
      dispatch({type: 'SET_ERROR', payload: error.message})
    }
  }

  const handleDownloadReport = (report, format) => {
    const filename = `${report.courseCode}_Report.${format}`
    alert(`Downloading ${filename}...`)
  }

  const handleViewReport = (report) => {
    alert(`Viewing report for ${report.courseName} (${report.courseCode})`)
  }

  const handleOpenProfile = () => {
    setShowProfileModal(true)
  }

  const handleMenuClick = () => {
    setSidebarOpen(!sidebarOpen)
  }

  const handleCloseSidebar = () => {
    setSidebarOpen(false)
  }
  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('demo') === 'true' && !state.user) {
      // Create a mock user for development
      const mockUser = {
        id: 'demo-user-123',
        email: 'demo@clomapper.com'
      };
      const mockProfile = {
        name: 'Demo User',
        email: 'demo@clomapper.com',
        faculty: 'Engineering',
        department: 'Computer Science',
        position: 'Senior Lecturer'
      };
      handleLogin(mockUser, mockProfile);
    }
  }, []);
  if (state.loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    )
  }

  if (!state.user) {
    return <Login onLogin={handleLogin} />
  }

  return (
    <Router>
      <div className="min-h-screen bg-gray-50">
        <Header
          onMenuClick={handleMenuClick}
          user={state.user}
          onLogout={handleLogout}
          onOpenProfile={handleOpenProfile}
        />
        
        <div className="flex">
          <Sidebar
            isOpen={sidebarOpen}
            onClose={handleCloseSidebar}
            user={state.user}
            onLogout={handleLogout}
          />
          
          <motion.main
            className={`flex-1 p-6 transition-all duration-300 ${
              sidebarOpen ? 'lg:ml-64' : 'ml-0'
            }`}
            initial={{opacity: 0, y: 20}}
            animate={{opacity: 1, y: 0}}
            transition={{duration: 0.5}}
          >
            <Routes>
              <Route path="/login" element={<Navigate to="/dashboard" replace />} />
              <Route 
                path="/dashboard" 
                element={
                  <CourseSelector
                    courses={state.courses}
                    activeCourse={state.activeCourse}
                    onSelectCourse={handleSelectCourse}
                    onDeleteCourse={handleDeleteCourse}
                    onCompleteReport={handleCompleteReport}
                  />
                } 
              />
              <Route 
                path="/profile" 
                element={
                  <Profile
                    user={state.profile || state.user}
                    onUpdateProfile={handleUpdateProfile}
                  />
                } 
              />
              <Route 
                path="/archived-reports" 
                element={
                  <ArchivedReports
                    archivedReports={state.archivedReports}
                    onDownloadReport={handleDownloadReport}
                    onViewReport={handleViewReport}
                  />
                } 
              />
              <Route path="/set-plo" element={<SetPLO />} />
              <Route path="/" element={<AssessmentPlan />} />
              <Route path="/slt-weightage" element={<SLTWeightage />} />
              <Route path="/clo-plo-mapping" element={<CLOPLOMapping />} />
              <Route path="/marks-entry" element={<MarksEntry />} />
              <Route path="/course-performance" element={<CoursePerformance />} />
              <Route path="/clo-analysis" element={<CLOAnalysis />} />
              <Route path="/plo-analysis" element={<PLOAnalysis />} />
              <Route path="/failure-report" element={<FailureReport />} />
            </Routes>
          </motion.main>
        </div>

        {/* Profile Modal */}
        <ProfileModal
          isOpen={showProfileModal}
          onClose={() => setShowProfileModal(false)}
          user={state.profile || state.user}
          onUpdateProfile={handleUpdateProfile}
        />

        {showExportModal && (
          <ReportExport
            course={exportCourse}
            onExport={handleExportReport}
            onClose={() => setShowExportModal(false)}
          />
        )}

        {state.error && (
          <div className="fixed bottom-4 right-4 bg-red-50 border border-red-200 rounded-lg p-4 shadow-lg">
            <p className="text-red-800">{state.error}</p>
            <button
              onClick={() => dispatch({type: 'SET_ERROR', payload: null})}
              className="mt-2 text-sm text-red-600 hover:text-red-800"
            >
              Dismiss
            </button>
          </div>
        )}
      </div>
    </Router>
  )
}

function App() {
  return (
    <AppProvider>
      <AppContent />
    </AppProvider>
  )
}

export default App